import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitWidthIcon from '@mui/icons-material/FitScreenOutlined';
import { downloadBlob } from '../../utils/downloadBlob';

const PdfPages = lazy(() => import('./PdfPages'));

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

const readBlob = (blob: Blob): Promise<ArrayBuffer> =>
  typeof blob.arrayBuffer === 'function'
    ? blob.arrayBuffer()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
      });

export interface PdfViewerSource {
  title: string;
  filename: string;
  /** Loads the PDF. The same bytes are shown and downloaded. */
  load: () => Promise<Blob>;
}

/** In-app PDF viewer: never a new tab. Full screen on phones; every page
 * scrolls; zoom; Download (the exact PDF shown), Print where the browser
 * supports it, Close. */
export const PdfViewerDialog = ({ source, onClose }: { source: PdfViewerSource | null; onClose: () => void }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [blob, setBlob] = useState<Blob | null>(null);
  const [data, setData] = useState<ArrayBuffer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pages, setPages] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!source) return;
    let cancelled = false;
    setBlob(null);
    setData(null);
    setError(null);
    setPages(null);
    setZoom(1);
    source
      .load()
      .then(async (b) => {
        const buffer = await readBlob(b);
        if (cancelled) return;
        setBlob(b);
        setData(buffer);
      })
      .catch((err: unknown) => !cancelled && setError(err instanceof Error ? err.message : 'The PDF could not be generated.'));
    return () => {
      cancelled = true;
    };
  }, [source, attempt]);

  const objectUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  const print = () => {
    if (!objectUrl) return;
    const frame = document.createElement('iframe');
    frame.style.position = 'fixed';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    frame.src = objectUrl;
    frame.onload = () => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } catch {
        setError('Printing isn’t supported in this browser. Download the PDF and print it from there.');
      }
      setTimeout(() => frame.remove(), 60_000);
    };
    document.body.appendChild(frame);
  };

  const zoomIndex = ZOOM_STEPS.indexOf(zoom);

  return (
    <Dialog
      open={!!source}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: fullScreen ? '100%' : '92vh', display: 'flex', flexDirection: 'column' } }}
      aria-labelledby="pdf-viewer-title"
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography id="pdf-viewer-title" variant="subtitle1" fontWeight={600} noWrap>
            {source?.title}
          </Typography>
          {pages !== null && (
            <Typography variant="caption" color="text.secondary">
              {pages} page{pages === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
        <Tooltip title="Zoom out">
          <span>
            <IconButton aria-label="Zoom out" disabled={!data || zoomIndex <= 0} onClick={() => setZoom(ZOOM_STEPS[zoomIndex - 1])}>
              <ZoomOutIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="body2" sx={{ minWidth: 44, textAlign: 'center' }} aria-live="polite">
          {Math.round(zoom * 100)}%
        </Typography>
        <Tooltip title="Zoom in">
          <span>
            <IconButton
              aria-label="Zoom in"
              disabled={!data || zoomIndex >= ZOOM_STEPS.length - 1}
              onClick={() => setZoom(ZOOM_STEPS[zoomIndex + 1])}
            >
              <ZoomInIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Fit to width">
          <span>
            <IconButton aria-label="Fit to width" disabled={!data || zoom === 1} onClick={() => setZoom(1)}>
              <FitWidthIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Print">
          <span>
            <IconButton aria-label="Print" disabled={!objectUrl} onClick={print}>
              <PrintIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          disabled={!blob}
          onClick={() => blob && source && downloadBlob(blob, source.filename)}
        >
          Download
        </Button>
        <IconButton aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider />
      <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: 'grey.200' }} data-testid="pdf-viewer-body">
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => setAttempt((a) => a + 1)}>
                  Try again
                </Button>
              }
            >
              {error}
            </Alert>
          </Box>
        )}
        {!data && !error && (
          <Stack alignItems="center" spacing={1.5} sx={{ p: 6 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Preparing the PDF…
            </Typography>
          </Stack>
        )}
        {data && (
          <Suspense
            fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={28} />
              </Box>
            }
          >
            <PdfPages data={data} zoom={zoom} onLoaded={setPages} />
          </Suspense>
        )}
      </Box>
    </Dialog>
  );
};
